export interface RentalQuote {
  monthlyRate: number
  totalRates: number | null
  depositMaximum: number | null
  downPaymentMaximum: number | null
  breakdown: {
    recoveredAmount: number
    servicesAndFinance: number
    finalResidual: number
  } | null
}

function toAmount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed
  const amount = Number(normalized)
  return Number.isFinite(amount) ? amount : null
}

export function parseRentalQuote(value: unknown): RentalQuote | null {
  if (!value || typeof value !== 'object') return null
  const response = value as Record<string, unknown>
  const monthlyRate = toAmount(response.IMPRATA)
  if (monthlyRate === null || monthlyRate < 0) return null

  const rawBreakdown = response.riepilogoCalcolo
  let breakdown: RentalQuote['breakdown'] = null
  if (rawBreakdown && typeof rawBreakdown === 'object' && !Array.isArray(rawBreakdown)) {
    const summary = rawBreakdown as Record<string, unknown>
    const totalRates = toAmount(summary.totaleRate)
    const recoveredAmount = toAmount(summary.quotaAmmortamento)
    const financeAmount = toAmount(summary.oneriFinanziari)
    const serviceAmount = toAmount(summary.quotaServizi)
    const finalResidual = toAmount(summary.valoreResiduoFinale)
    if ([totalRates, recoveredAmount, financeAmount, serviceAmount, finalResidual]
      .every((amount) => amount !== null && amount >= 0)) {
      breakdown = {
        recoveredAmount: recoveredAmount!,
        servicesAndFinance: Math.round((financeAmount! + serviceAmount!) * 100) / 100,
        finalResidual: finalResidual!,
      }
    }
  }

  return {
    monthlyRate,
    depositMaximum: toAmount(response.cauzioneMassima),
    downPaymentMaximum: toAmount(response.accontoMassimo),
    totalRates: rawBreakdown && typeof rawBreakdown === 'object' && !Array.isArray(rawBreakdown)
      ? toAmount((rawBreakdown as Record<string, unknown>).totaleRate)
      : null,
    breakdown,
  }
}

export function parseVehiclePriceExcludingVat(value: unknown): number | null {
  const amount = toAmount(value)
  return amount !== null && amount > 0 ? amount : null
}

// Il configuratore precedente consentiva un anticipo massimo pari a metà
// della somma delle rate per la durata selezionata.
export function calculateMaxDownPayment(monthlyRate: number, durationMonths: number): number | null {
  if (!Number.isFinite(monthlyRate) || monthlyRate < 0 || !Number.isInteger(durationMonths) || durationMonths <= 0) return null
  return monthlyRate * durationMonths / 2
}

export function calculateMaxDownPaymentFromTotal(totalRates: number | null): number | null {
  if (totalRates === null || !Number.isFinite(totalRates) || totalRates < 0) return null
  return totalRates / 2
}

export function formatRentalCurrency(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    useGrouping: 'always',
    maximumFractionDigits: 2,
  }).format(value)
}
